/**
 * Standalone Document Detail Page
 *
 * Smart redirect: if the document belongs to a container, redirects to the
 * unified viewer at /shipper/containers/{containerId}/documents/{documentId}?mode=manager.
 * Otherwise renders the unified viewer directly without container context.
 */

import { useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { Loading02 } from "@untitledui/icons";
import { useDocument } from "@/hooks/api/use-documents";
import UnifiedDocumentViewerPage from "@/pages/shipper/containers/documents/[documentId]";

export const ShipperDocumentDetailPage = () => {
    const { documentId } = useParams<{ documentId: string }>();
    const navigate = useNavigate();
    const { data: document, isLoading, error } = useDocument(documentId || "");

    useEffect(() => {
        if (document?.container_id) {
            // Redirect to the container-scoped unified viewer
            navigate(
                `/shipper/containers/${document.container_id}/documents/${document.document_id}?mode=manager`,
                { replace: true }
            );
        }
    }, [document, navigate]);

    // While loading or redirecting, show spinner
    if (isLoading || document?.container_id) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Loading02 className="size-8 text-brand-600 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen items-center justify-center">
                <p className="text-sm text-error-600">Document not found</p>
            </div>
        );
    }

    // No container — render the unified viewer directly (works with undefined containerId)
    return <UnifiedDocumentViewerPage />;
};
